import { apiClient } from './api-client';

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Individual personal data item from the API
 */
export interface PersonalDataItem {
    success: boolean;
    originalValue: string | null;
    error?: string;
}

/**
 * API response for getting personal data values
 * Structure: { success: true, personalData: { "vaultId": { success: true, originalValue: "value" } } }
 * Example: { "success": true, "personalData": { "62": { "success": true, "originalValue": "saxena" } } }
 */
export interface PersonalDataResponse {
    success: boolean;
    personalData: Record<string, PersonalDataItem>;
    error?: string;
}

// ============================================================================
// PERSONAL DATA SERVICE CLASS
// ============================================================================

export class PersonalDataService {
    private baseUrl: string;

    constructor() {
        this.baseUrl = '/portal';
    }

    /**
     * Get personal data values for specific vault IDs
     */
    async getPersonalDataValues(consentHeaderId: number, vaultIds: string[]): Promise<PersonalDataResponse> {
        try {
            const url = `${this.baseUrl}/consent/${consentHeaderId}/personal-data`;
            const response = await apiClient.post<PersonalDataResponse>(url, { vaultIds });

            if (!response.success) {
                throw new Error(response.error || 'Failed to fetch personal data values');
            }

            return response;
        } catch (error) {
            console.error('Error fetching personal data values:', error);
            throw error;
        }
    }

    /**
     * Get personal data values with error handling and loading states
     */
    async getPersonalDataValuesWithState(consentHeaderId: number, vaultIds: string[]): Promise<{
        data: PersonalDataResponse | null;
        loading: boolean;
        error: string | null;
    }> {
        try {
            const data = await this.getPersonalDataValues(consentHeaderId, vaultIds);
            return {
                data,
                loading: false,
                error: null
            };
        } catch (error: any) {
            return {
                data: null,
                loading: false,
                error: error.message || 'Failed to load personal data values'
            };
        }
    }

    /**
     * Helper method to get a specific vault's personal data from the response
     */
    getVaultPersonalData(response: PersonalDataResponse, vaultId: string): PersonalDataItem | null {
        if (!response.success || !response.personalData) {
            return null;
        }
        return response.personalData[vaultId] || null;
    }
}

// Create and export a singleton instance
export const personalDataService = new PersonalDataService();

// Export default
export default personalDataService;
